import { keepPreviousData, useQueries, useQuery, type QueryKey } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClient, ApiClientKind } from '@/api/client'
import type { BreakdownDimension, RevenueBasis, RevenueBreakdown, RevenueGranularity } from '@/api/revenue'
import type { AttributionModel, Envelope } from '@/api/types'
import { addDays, dayCount } from '@/lib/filters'
import type { GlobalFilters } from '@/lib/filters'
import { filtersKey } from '@/lib/useGlobalFilters'

/*
 * The page's requests, one per route and panel. Each key carries the basis, so a gross
 * answer can never stand in for a net one; while new filters load, the previous figures
 * stay on screen (marked busy) only when the filters are all that changed.
 */

export const revenueKeys = {
  all: ['revenue'] as const,
  /** The route, the client, the filters, the basis, then the route's own parameters. */
  route: (route: string, kind: ApiClientKind, filters: GlobalFilters, basis: RevenueBasis, ...own: readonly unknown[]) =>
    [...revenueKeys.all, route, kind, filtersKey(filters), basis, ...own] as const,
}

/** Where the filters sit in a `route` key: the one part a placeholder may differ in. */
const FILTERS_AT = revenueKeys.all.length + 2

function differOnlyInFilters(next: QueryKey, previous: QueryKey): boolean {
  return next.length === previous.length && next.every((part, index) => index === FILTERS_AT || part === previous[index])
}

function placeholderFor(key: QueryKey) {
  return <T>(previous: T | undefined, query: { queryKey: QueryKey } | undefined) =>
    query && differOnlyInFilters(key, query.queryKey) ? keepPreviousData(previous) : undefined
}

function useRevenueRoute<T>(
  route: string,
  filters: GlobalFilters,
  basis: RevenueBasis,
  own: readonly unknown[],
  read: (client: ApiClient) => Promise<Envelope<T>>,
  enabled = true,
) {
  const client = useApiClient()
  const queryKey = revenueKeys.route(route, client.kind, filters, basis, ...own)
  return useQuery({ queryKey, queryFn: () => read(client), placeholderData: placeholderFor(queryKey), enabled })
}

export function useRevenueSummary(filters: GlobalFilters, basis: RevenueBasis) {
  return useRevenueRoute('summary', filters, basis, [], (client) => client.revenueSummary({ filters, basis }))
}

export function useRevenueSeries(filters: GlobalFilters, basis: RevenueBasis, granularity: RevenueGranularity) {
  return useRevenueRoute('trend', filters, basis, [granularity], (client) => client.revenueSeries({ filters, basis, granularity }))
}

export function useRevenueBreakdown(
  filters: GlobalFilters,
  basis: RevenueBasis,
  by: BreakdownDimension,
  { limit, model, enabled = true }: { limit?: number; model?: AttributionModel; enabled?: boolean } = {},
) {
  return useRevenueRoute(
    'breakdown',
    filters,
    basis,
    [by, limit ?? null, model ?? null],
    (client) => client.revenueBreakdown({ filters, basis, by, limit, model }),
    enabled,
  )
}

export function useRevenueTiming(filters: GlobalFilters, basis: RevenueBasis) {
  return useRevenueRoute('timing', filters, basis, [], (client) => client.revenueTiming({ filters, basis }))
}

// --- the weekly panels ------------------------------------------------------------------------

/** The most weeks a weekly panel reads one by one: 13 covers a 90-day range. */
export const MAX_WEEKS = 13

export interface WeekWindow {
  from: string
  to: string
}

/**
 * The range in weeks as the trend's `granularity=week` counts them: seven days from `from`,
 * the last cut to the range.
 */
export function weekWindows(range: { from: string; to: string }): WeekWindow[] {
  const weeks: WeekWindow[] = []
  const days = dayCount(range.from, range.to)
  for (let offset = 0; offset < days; offset += 7) {
    const from = addDays(range.from, offset)
    const last = addDays(from, 6)
    weeks.push({ from, to: last < range.to ? last : range.to })
  }
  return weeks
}

export interface WeeklyBreakdown {
  week: WeekWindow
  data: RevenueBreakdown | undefined
}

/**
 * One breakdown per week of the range (no comparison: the panel draws none), so a weekly
 * panel plots the API's own figures for each week. Held while `weeks` is empty (the
 * range is not known yet, or it is longer than `MAX_WEEKS`).
 */
export function useWeeklyBreakdowns(
  filters: GlobalFilters,
  basis: RevenueBasis,
  by: BreakdownDimension,
  weeks: readonly WeekWindow[],
  limit?: number,
) {
  const client = useApiClient()
  // The weeks are read with no comparison whatever the filters ask, so the comparison is no part of their key.
  const scope: GlobalFilters = { ...filters, compare: 'none' }
  const reads = useQueries({
    queries: weeks.map((week) => ({
      queryKey: revenueKeys.route('breakdown-week', client.kind, scope, basis, by, limit ?? null, week.from, week.to),
      queryFn: () => client.revenueBreakdown({ filters: scope, basis, by, limit, window: week, withoutComparison: true }),
    })),
  })
  return {
    weeks: weeks.map((week, index): WeeklyBreakdown => ({ week, data: reads[index]?.data?.data })),
    isPending: reads.some((read) => read.isPending),
    isError: reads.some((read) => read.isError),
    isFetching: reads.some((read) => read.isFetching),
  }
}
